/**
 * Data Description API Service
 * 
 * Service for analyzing uploaded files and getting detailed information
 * about their structure, content, and quality issues.
 */

import { httpClient, unwrapApiResult } from './httpClient';
import { apiPath } from '../config/env';
import { BackendDQRule } from '../types/dqRules';
import { authManager } from './authManager';
import { acquireTranslationToken } from './tokenUtils';

export interface DataDescriptionResponse {
    file_name: string;
    description: string | null;
    column_description: {
        [columnName: string]: {
            data_type: string;
            sample_values: string[];
            description: string;
            has_quality_issues: boolean;
            dq_rules?: BackendDQRule[];
        };
    };
    table_type?: string;
    table_type_justification?: string;
    target_language: string | null;
    fields_translated: number | null;
}

export interface DataDescriptionAnalysis {
    fileName: string;
    description: string | null;
    columns: Array<{
        name: string;
        dataType: string;
        sampleValues: string[];
        description: string;
        hasQualityIssues: boolean;
        dq_rules?: BackendDQRule[];
    }>;
    qualityIssuesCount: number;
    allDqRules?: BackendDQRule[];
    tableType?: string;
    tableTypeJustification?: string;
    targetLanguage?: string | null;
    fieldsTranslated?: number | null;
}

class DataDescriptionService {
    /**
     * Analyze a file and get detailed description
     */
    async analyzeFile(file: File, targetLanguage?: string, columns?: string[]): Promise<DataDescriptionAnalysis> {
        // Validate file type
        const validTypes = [
            'text/csv',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ];

        const isValidType = validTypes.includes(file.type) ||
            file.name.toLowerCase().match(/\.(csv|xlsx|xls)$/);

        if (!isValidType) {
            throw new Error('Please upload a CSV or Excel file');
        }

        // Check file size (500MB limit)
        const maxSize = 500 * 1024 * 1024; // 500MB
        if (file.size > maxSize) {
            throw new Error('File size must be less than 500MB');
        }

        // Create FormData for file upload
        const formData = new FormData();
        formData.append('file', file);

        // Make API request — user_email is a query parameter, not form field
        const userEmail = encodeURIComponent(authManager.getUserEmail());
        let queryString = `user_email=${userEmail}`;
        if (targetLanguage && targetLanguage !== 'en') {
            queryString += `&target_language=${encodeURIComponent(targetLanguage)}`;
        }
        if (columns && columns.length > 0) {
            queryString += `&columns=${encodeURIComponent(columns.join(','))}`;
        }

        // When translation is requested, a Bearer token is required
        const headers: Record<string, string> = {};
        if (targetLanguage && targetLanguage !== 'en') {
            const token = await acquireTranslationToken();
            headers['Authorization'] = `Bearer ${token}`;
        }

        const result = await httpClient.post<DataDescriptionResponse>(
            apiPath(`data-description/analyze?${queryString}`),
            formData,
            { headers }
        );

        const response = unwrapApiResult(result);

        // Transform response to our internal format
        const transformedColumns = Object.entries(response.column_description).map(([name, info]) => ({
            name,
            dataType: info.data_type,
            sampleValues: info.sample_values,
            description: info.description,
            hasQualityIssues: info.has_quality_issues
        }));

        const qualityIssuesCount = transformedColumns.filter(col => col.hasQualityIssues).length;

        return {
            fileName: response.file_name,
            description: response.description,
            columns: transformedColumns,
            qualityIssuesCount,
            tableType: response.table_type,
            tableTypeJustification: response.table_type_justification,
            targetLanguage: response.target_language,
            fieldsTranslated: response.fields_translated
        };
    }

    /**
     * Analyze a delta table via backend (no file upload) using table full name.
     * Backend endpoint: POST /api/v1/data-description/analyze?table=<catalog.schema.table>
     * Accepts multipart/form-data even without a file; we send an empty FormData to satisfy content-type.
     * Response shape assumed to match DataDescriptionResponse (columns + description).
     */
    async analyzeTable(fullTableName: string, targetLanguage?: string, columns?: string[]): Promise<DataDescriptionAnalysis> {
        if (!fullTableName || !fullTableName.includes('.')) {
            throw new Error('Invalid table name. Expected catalog.schema.table');
        }
        const formData = new FormData();
        const userEmail = encodeURIComponent(authManager.getUserEmail());
        let queryString = `table=${encodeURIComponent(fullTableName)}&user_email=${userEmail}`;
        if (targetLanguage && targetLanguage !== 'en') {
            queryString += `&target_language=${encodeURIComponent(targetLanguage)}`;
        }
        if (columns && columns.length > 0) {
            queryString += `&columns=${encodeURIComponent(columns.join(','))}`;
        }

        // When translation is requested, a Bearer token is required
        const headers: Record<string, string> = {};
        if (targetLanguage && targetLanguage !== 'en') {
            const token = await acquireTranslationToken();
            headers['Authorization'] = `Bearer ${token}`;
        }

        try {
            const result = await httpClient.post<DataDescriptionResponse>(
                apiPath(`data-description/analyze?${queryString}`),
                formData,
                { headers }
            );
            const response = unwrapApiResult(result);
            const transformedColumns = Object.entries(response.column_description).map(([name, info]) => ({
                name,
                dataType: info.data_type,
                sampleValues: info.sample_values,
                description: info.description,
                hasQualityIssues: info.has_quality_issues,
                dq_rules: info.dq_rules || [] // Extract DQ rules for each column
            }));

            // Extract all DQ rules from all columns for the existing rules feature
            const allDqRules = Object.values(response.column_description)
                .flatMap(info => info.dq_rules || []);

            return {
                fileName: response.file_name || fullTableName,
                description: response.description,
                columns: transformedColumns,
                qualityIssuesCount: transformedColumns.filter(c => c.hasQualityIssues).length,
                allDqRules,
                tableType: response.table_type,
                tableTypeJustification: response.table_type_justification,
                targetLanguage: response.target_language,
                fieldsTranslated: response.fields_translated
            };
        } catch (err: any) {
            // Check multiple potential locations for the detail message
            const detail: string | undefined = err?.details?.detail || err?.details || err?.detail ||
                (typeof err?.details === 'string' ? err.details : undefined);
            if (typeof detail === 'string') {
                if (detail.includes('empty table') || detail.includes('no data')) {
                    throw new Error('The selected table appears to be empty or contains no data. Please choose a table with data to analyze.');
                } else if (detail.includes('exceeds Databricks inline limit (25 MB)')) {
                    throw new Error('Table size exceeds Databricks inline limit (25 MB). Please try with a smaller dataset.');
                } else if (detail.includes('authentication') || detail.includes('unauthorized')) {
                    throw new Error('Authentication error. Please refresh the page and try again.');
                } else if (detail.includes('connection') || detail.includes('timeout')) {
                    throw new Error('Connection timeout. Please check your network connection and try again.');
                }
            }

            // For 500 errors, provide a general server error message
            if (err?.status === 500) {
                throw new Error('Backend server error occurred while analyzing the table. Please try again or contact your administrator.');
            }

            throw err;
        }
    }

    /**
     * Export analysis results as JSON
     */
    exportAsJson(analysis: DataDescriptionAnalysis): void {
        const dataStr = JSON.stringify(analysis, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${analysis.fileName}_analysis.json`;
        link.click();
        URL.revokeObjectURL(url);
    }

    /**
     * Export analysis results as CSV
     */
    exportAsCsv(analysis: DataDescriptionAnalysis): void {
        const csvContent = [
            ['Column Name', 'Data Type', 'Description', 'Sample Values', 'Quality Issues'],
            ...analysis.columns.map(col => [
                col.name,
                col.dataType,
                col.description,
                col.sampleValues.join('; '),
                col.hasQualityIssues ? 'Yes' : 'No'
            ])
        ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${analysis.fileName}_analysis.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }
}

export const dataDescriptionService = new DataDescriptionService();